import { useMemo, useState } from 'react';
import Header from 'src/header';
import { Card } from 'src/components/Card';
import { Specialty } from 'src/shared/types';
import AdminHeader from './AdminHeader';
import { useSpecialties } from 'src/hooks/useSpecialties';
import Loading from 'src/components/Loading';
import { ColumnProps, Table } from 'src/components/table/Table';
import { SortDirection, useTableData } from 'src/components/table/useTableData';
import Button from 'src/components/Button';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';

const SpecialtiesConfig = () => {
  const organization = useCurrentOrganization();
  const { fetchSpecialties,  addSpecialty, deleteSpecialty, error: specialtyError } = useSpecialties();
  const [ newSpecialty, setNewSpecialty ] = useState('');

  const columns: ColumnProps<Specialty>[] = useMemo(() => [
    {
      title: "Name",
      sortKey: 'name',
      convertToCellData: (specialty: Specialty) => (
        <span className="text-sm font-medium text-gray-900">{specialty.name}</span>
      )
    },
    {
      title: "Created At",
      sortKey: 'created_at',
      convertToCellData: (specialty: Specialty) => (
        <span className="text-sm text-gray-900">
          {new Date(specialty.created_at).toLocaleString('en-US', {
            timeZone: 'America/New_York',
            timeZoneName: "short",
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
        </span>
      )
    }
  ], []);

  const tableData = useTableData({
    tableId: 'specialties',
    getData: fetchSpecialties,
    initialSortField: 'name',
    initialSortDirection: SortDirection.ASC,
  });

  const handleAddSpecialty = async () => {
    if (!newSpecialty.trim()) return;

    await addSpecialty(newSpecialty.trim());
    setNewSpecialty('');
    tableData.refresh();
  };

  const handleDeleteSpecialty = async (specialty: string) => {
    await deleteSpecialty(specialty);
    tableData.refresh();
  };

  const body = tableData.loading ? <Loading message="Loading specialties..." /> : (
    <div className="space-y-6">
      <Card className="w-full p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Add New Specialty</h2>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={newSpecialty}
            onChange={(e) => setNewSpecialty(e.target.value)}
            placeholder="Enter specialty name"
            className="flex-grow p-2 border border-gray-300 rounded"
          />
          <Button
            onClick={handleAddSpecialty}
            text="Add Specialty"
            icon={<PlusIcon className="w-4 h-4 stroke-2" />}
          />
        </div>
        {specialtyError && <p className="text-red-500 mt-2">{specialtyError}</p>}
      </Card>

      <Card className="w-full p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Specialties</h2>
        <Table
          tableData={tableData}
          columns={columns}
          rowId={(specialty: Specialty) => `${specialty.id}-${specialty.name}`}
          headerClass="bg-gray-50"
          headerColor={false}
          onDelete={(specialty: Specialty) => {
            handleDeleteSpecialty(specialty.name);
          }}
        />
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <Header isAdminPage={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader
          title="Specialties Configuration"
          description="Configure the specialties for your organization"
          organization={organization}
        />
        {body}
      </div>
    </div>
  );
};

export default SpecialtiesConfig;
